import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../config/api-config';
import './BoardComponents.css';

const Arrow = ({ 
  id, 
  initialPoints = { 
    start: { x: 100, y: 100 }, 
    control: { x: 200, y: 50 }, 
    end: { x: 300, y: 100 } 
  }, 
  initialPosition = { x: 0, y: 0 },
  onUpdate, 
  onDelete, 
  onDragStart, 
  onDragMove,
  onDragEnd 
}) => {
  const { boardId } = useParams(); // Get boardId from URL params
  const [points, setPoints] = useState(initialPoints);
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [activeDragPoint, setActiveDragPoint] = useState(null); // 'start', 'control', 'end', or null
  const arrowRef = useRef(null);

  // Calculate the SVG path for the bezier curve arrow
  const getArrowPath = () => {
    const { start, control, end } = points;
    
    // Calculate the bezier curve path
    const path = `M ${start.x} ${start.y} Q ${control.x} ${control.y}, ${end.x} ${end.y}`;
    
    // Calculate the arrow head
    // First, get the direction from the control point to the end point
    const dx = end.x - control.x;
    const dy = end.y - control.y;
    
    // Normalize the direction vector
    const length = Math.sqrt(dx * dx + dy * dy);
    const unitDx = dx / length;
    const unitDy = dy / length;
    
    // Calculate perpendicular vectors for the arrow head
    const perpDx = -unitDy;
    const perpDy = unitDx;
    
    // Arrow head size
    const arrowSize = 10;
    
    // Calculate the two points of the arrow head
    const arrowPoint1 = {
      x: end.x - arrowSize * unitDx + arrowSize * perpDx / 2,
      y: end.y - arrowSize * unitDy + arrowSize * perpDy / 2
    };
    
    const arrowPoint2 = {
      x: end.x - arrowSize * unitDx - arrowSize * perpDx / 2,
      y: end.y - arrowSize * unitDy - arrowSize * perpDy / 2
    };
    
    // Add the arrow head to the path
    const arrowPath = `${path} L ${arrowPoint1.x} ${arrowPoint1.y} M ${end.x} ${end.y} L ${arrowPoint2.x} ${arrowPoint2.y}`;
    
    return arrowPath;
  };

  // Handle dragging the entire arrow
  const handleMouseDown = (e) => {
    // Only start dragging if we click on the arrow path
    if (e.target.classList.contains('arrow-path')) {
      setIsDragging(true);
      
      // Notify parent component that dragging has started
      if (onDragStart) {
        onDragStart(id);
      }
      
      // Get the board container to calculate relative position
      const boardContent = document.querySelector('.board-content');
      const boardRect = boardContent.getBoundingClientRect();
      
      // Calculate the mouse position relative to the board
      const mouseX = e.clientX - boardRect.left;
      const mouseY = e.clientY - boardRect.top;
      
      // Calculate the offset from the mouse position to the start point
      // This will be used to maintain the relative position during dragging
      setDragOffset({
        x: mouseX - points.start.x,
        y: mouseY - points.start.y
      });
      
      // Prevent text selection during drag
      e.preventDefault();
    }
  };

  // Handle dragging a control point
  const handlePointMouseDown = (e, pointType) => {
    e.stopPropagation(); // Prevent event from bubbling up to the arrow container
    
    setActiveDragPoint(pointType);
    
    // Notify parent component that dragging has started
    if (onDragStart) {
      onDragStart(id);
    }
    
    // Prevent text selection during drag
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    // Get the board container to calculate relative position
    const boardContent = document.querySelector('.board-content');
    
    const boardRect = boardContent.getBoundingClientRect();
    
    if (isDragging) {
      // When dragging the entire arrow, move all points together
      // Calculate the mouse position relative to the board
      const mouseX = e.clientX - boardRect.left;
      const mouseY = e.clientY - boardRect.top;
      
      // Calculate the new position for the start point
      const newStartX = mouseX - dragOffset.x;
      const newStartY = mouseY - dragOffset.y;
      
      // Calculate the movement delta from the current start point
      const deltaX = newStartX - points.start.x;
      const deltaY = newStartY - points.start.y;
      
      // Calculate new positions for all points
      const newStartPoint = { x: newStartX, y: newStartY };
      const newControlPoint = { x: points.control.x + deltaX, y: points.control.y + deltaY };
      const newEndPoint = { x: points.end.x + deltaX, y: points.end.y + deltaY };
      
      // Apply constraints to keep all points within the board
      const constrainedStartPoint = {
        x: Math.max(0, Math.min(newStartPoint.x, boardRect.width)),
        y: Math.max(0, Math.min(newStartPoint.y, boardRect.height))
      };
      
      const constrainedControlPoint = {
        x: Math.max(0, Math.min(newControlPoint.x, boardRect.width)),
        y: Math.max(0, Math.min(newControlPoint.y, boardRect.height))
      };
      
      const constrainedEndPoint = {
        x: Math.max(0, Math.min(newEndPoint.x, boardRect.width)),
        y: Math.max(0, Math.min(newEndPoint.y, boardRect.height))
      };
      
      // Move all points by the constrained delta
      setPoints({
        start: constrainedStartPoint,
        control: constrainedControlPoint,
        end: constrainedEndPoint
      });
      
      // Notify parent component that dragging is in progress
      // This updates the timestamp on every frame during dragging
      if (onDragMove) {
        onDragMove(id);
      }
    } else if (activeDragPoint) {
      // When dragging a control point, only move that point
      // Calculate new point position relative to the board container
      const newX = e.clientX - boardRect.left;
      const newY = e.clientY - boardRect.top;
      
      // Apply constraints to keep the point within the board
      const constrainedX = Math.max(0, Math.min(newX, boardRect.width));
      const constrainedY = Math.max(0, Math.min(newY, boardRect.height));
      
      // Update the active point with constrained values
      setPoints(prevPoints => ({
        ...prevPoints,
        [activeDragPoint]: { x: constrainedX, y: constrainedY }
      }));
      
      // Notify parent component that dragging is in progress
      // This updates the timestamp on every frame during dragging
      if (onDragMove) {
        onDragMove(id);
      }
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      setIsDragging(false);
      
      // Calculate a representative position for the arrow (using start point)
      const arrowPosition = {
        x: points.start.x,
        y: points.start.y
      };
      
      // Update local position state immediately
      setPosition(arrowPosition);
      
      // Notify parent component that dragging has ended and pass the position
      if (onDragEnd) {
        onDragEnd(arrowPosition);
      }
      
      // Update the server with the new position and points
      updateArrowData();
    } else if (activeDragPoint) {
      setActiveDragPoint(null);
      
      // Calculate a representative position for the arrow (using start point)
      const arrowPosition = {
        x: points.start.x,
        y: points.start.y
      };
      
      // Update local position state immediately
      setPosition(arrowPosition);
      
      // Notify parent component that dragging has ended and pass the position
      if (onDragEnd) {
        onDragEnd(arrowPosition);
      }
      
      // Update the server with the new points
      updateArrowData();
    }
  };

  // Update arrow data on the server and locally
  const updateArrowData = () => {
    if (id && boardId && !id.toString().startsWith('temp-')) {
      // Calculate a representative position for the arrow (using start point)
      const arrowPosition = {
        x: points.start.x,
        y: points.start.y
      };
      
      // Update locally first for immediate feedback
      if (onUpdate) {
        onUpdate(id, { points, position: arrowPosition });
      }
      
      // Then update on the server
      (async () => {
        try {
          console.log('Updating arrow data:', { points });
          
          // Get auth token
          const { getIdToken } = await import('firebase/auth');
          const { auth } = await import('../../config/firebase-config');
          const token = await getIdToken(auth.currentUser);
          
          // Update points via API
          await api.put(
            `/api/boards/${boardId}/components/${id}/points`,
            { points },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          console.log('Arrow points updated via API successfully');
        } catch (err) {
          console.error('Error updating arrow points via API:', err);
          // No need for fallback as we already updated locally
        }
      })();
    }
  };
  // Handle delete
  const handleDelete = () => {
    if (onDelete) {
      onDelete(id);
    }
  };

  // Track the last updated points and position from props to detect changes
  const lastInitialPointsRef = useRef(initialPoints);
  const lastInitialPositionRef = useRef(initialPosition);
  
  // Effect to update position and points when they change from server
  // but not during active dragging
  useEffect(() => {
    // Skip updates during dragging
    if (isDragging || activeDragPoint) {
      return;
    }
    
    // Check if initialPoints has actually changed (deep comparison)
    const pointsChanged = JSON.stringify(initialPoints) !== JSON.stringify(lastInitialPointsRef.current);
    
    if (pointsChanged) {
      setPoints(initialPoints);
      lastInitialPointsRef.current = initialPoints;
    }
    
    // Check if initialPosition has changed
    const positionChanged = 
      initialPosition.x !== lastInitialPositionRef.current.x || 
      initialPosition.y !== lastInitialPositionRef.current.y;
    
    if (positionChanged) {
      setPosition(initialPosition);
      lastInitialPositionRef.current = initialPosition;
    }
  }, [initialPosition, initialPoints, isDragging, activeDragPoint]);

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging || activeDragPoint) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, activeDragPoint, points, position]);

  return (
    <div
      ref={arrowRef}
      className={`board-component arrow ${isDragging || activeDragPoint ? 'dragging' : ''}`}
      style={{
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none', // Make the container not capture mouse events
      }}
    >
      <div 
        className="arrow-header"
        style={{
          position: 'absolute',
          left: `${points.start.x + 10}px`,
          top: `${points.start.y - 30}px`,
          pointerEvents: 'auto', // Allow mouse events on the header
          opacity: 0, // Make the top bar transparent
        }}
      >
        <button className="delete-button" onClick={handleDelete}>×</button>
      </div>
      
      {/* SVG for the arrow path */}
      <svg 
        width="100%" 
        height="100%" 
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        <path
          d={getArrowPath()}
          className="arrow-path"
          stroke="white"
          strokeWidth="2"
          fill="none"
          style={{ pointerEvents: 'auto' }} // Allow mouse events on the path
          onMouseDown={handleMouseDown}
        />
      </svg>
      
      {/* Control points */}
      <div 
        className="control-point start-point"
        style={{ 
          position: 'absolute',
          left: `${points.start.x - 5}px`, 
          top: `${points.start.y - 5}px`,
          pointerEvents: 'auto' // Allow mouse events on control points
        }}
        onMouseDown={(e) => handlePointMouseDown(e, 'start')}
      />
      <div 
        className="control-point control-point-middle"
        style={{ 
          position: 'absolute',
          left: `${points.control.x - 5}px`, 
          top: `${points.control.y - 5}px`,
          pointerEvents: 'auto' // Allow mouse events on control points
        }}
        onMouseDown={(e) => handlePointMouseDown(e, 'control')}
      />
      <div 
        className="control-point end-point"
        style={{ 
          position: 'absolute',
          left: `${points.end.x - 5}px`, 
          top: `${points.end.y - 5}px`,
          pointerEvents: 'auto' // Allow mouse events on control points
        }}
        onMouseDown={(e) => handlePointMouseDown(e, 'end')}
      />
    </div>
  );
};

export default Arrow;
